import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import {
  LoteInventarioModalComponent,
  LoteInventarioModalData,
  LoteInventarioModalItem,
} from '../../../../../shared/components/lote-inventario-modal/lote-inventario-modal.component';
import {
  SerieInventarioModalComponent,
  SerieInventarioModalData,
  SerieInventarioModalItem,
} from '../../../../../shared/components/serie-inventario-modal/serie-inventario-modal.component';
import { ControleEstoqueListStruct } from '../../../../../shared/services/structs/produto/controle-estoque-list.struct';
import { NgxCurrencyDirective } from 'ngx-currency';
import { MaskService } from '../../../../../shared/services/mask.service';
import { SelectPesquisavelComponent } from '../../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { AbntPipe } from '../../../../../shared/pipes/abnt.pipe';
import { environment } from '../../../../../../environments/environment';

export interface ItemSheetLocalOpcao {
  idInventarioItem?: number;
  idLocalizacaoEstoque: number;
  nome: string;
  saldoInicial?: number;
  quantidadeInventariada?: number | null;
  lotes?: LoteInventarioModalItem[];
  series?: SerieInventarioModalItem[];
  confirmadoRastreabilidade?: boolean;
}

export interface ItemSheetPrefill {
  idLocalizacaoEstoque?: number;
  codigoLoteSerie?: string;
}

export interface ItemSheetResultado {
  idLocalizacaoEstoque: number;
  quantidade: number;
  lotes?: LoteInventarioModalItem[];
  series?: SerieInventarioModalItem[];
}

// Painel deslizante (de baixo para cima) para o produto encontrado/bipado: escolhe local,
// quantidade (com - / +), e lote/série quando aplicável, confirmando o lançamento.
@Component({
  selector: 'app-inventario-item-sheet',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    NgxCurrencyDirective,
    SelectPesquisavelComponent,
    AbntPipe,
  ],
  templateUrl: './inventario-item-sheet.component.html',
  styleUrl: './inventario-item-sheet.component.css',
})
export class InventarioItemSheetComponent implements OnInit, OnChanges {
  @Input() produto?: ControleEstoqueListStruct;
  @Input() locaisDisponiveis: ItemSheetLocalOpcao[] = [];
  @Input() isReadOnly = false;
  @Input() prefill?: ItemSheetPrefill;
  @Input() isCarregandoProduto = false;
  @Input() permiteSelecionarProduto = false;

  @Output() confirmar = new EventEmitter<ItemSheetResultado>();
  @Output() produtoSelecionado = new EventEmitter<any>();
  @Output() limpar = new EventEmitter<void>();
  @Output() fechar = new EventEmitter<void>();

  public readonly produtoApiUrl = `${environment.urlApiProduto}Lookup/listProdutoSelect`;
  public produtoControl = new FormControl<number | null>(null);
  public produtoPreload: any[] = [];
  public idLocalizacaoEstoque: number | null = null;
  public quantidade = 1;
  public rastreabilidadeConfirmada = false;

  public lotes: LoteInventarioModalItem[] = [];
  public series: SerieInventarioModalItem[] = [];

  // Máscara de quantidade com as casas decimais globais do usuário (token info).
  public quantidadeMask = { ...this.maskService.getDynamicDecimalMask(), allowNegative: false };

  constructor(
    private maskService: MaskService,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.configurarProdutoAtual(true);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['produto'] || changes['locaisDisponiveis'] || changes['prefill']) {
      this.configurarProdutoAtual(true);
    }
  }

  public definirProduto(produto: ControleEstoqueListStruct, locaisDisponiveis: ItemSheetLocalOpcao[]): void {
    this.produto = produto;
    this.locaisDisponiveis = locaisDisponiveis;
    this.configurarProdutoAtual(true);
  }

  public onProdutoSelecionado(selecao: any): void {
    const idProduto = typeof selecao === 'number' ? selecao : selecao?.idProduto;
    if (!idProduto || idProduto === this.produto?.idProduto) return;

    this.produtoControl.setValue(idProduto, { emitEvent: false });
    this.produtoSelecionado.emit(selecao);
  }

  private configurarProdutoAtual(aplicarPrefillCodigo: boolean): void {
    if (!this.produto) {
      this.produtoControl.setValue(null, { emitEvent: false });
      this.produtoPreload = [];
      this.idLocalizacaoEstoque = null;
      this.quantidade = 1;
      this.lotes = [];
      this.series = [];
      this.rastreabilidadeConfirmada = false;
      return;
    }

    this.produtoControl.setValue(this.produto.idProduto, { emitEvent: false });
    this.produtoPreload = [{ idProduto: this.produto.idProduto, descricao: this.produto.descricao }];
    this.idLocalizacaoEstoque = this.prefill?.idLocalizacaoEstoque
      ?? (this.locaisDisponiveis.length ? this.locaisDisponiveis[0].idLocalizacaoEstoque : null);

    this.carregarLocalSelecionado(aplicarPrefillCodigo);
  }

  public onLocalChange(): void {
    this.carregarLocalSelecionado(false);
  }

  private carregarLocalSelecionado(aplicarPrefillCodigo: boolean): void {
    const local = this.localAtual;
    this.quantidade = local?.quantidadeInventariada ?? 1;
    this.lotes = (local?.lotes ?? []).map(l => ({ ...l }));
    this.series = (local?.series ?? []).map(s => ({ ...s }));
    this.rastreabilidadeConfirmada = !!local?.confirmadoRastreabilidade;

    if (!aplicarPrefillCodigo) {
      return;
    }

    // Pré-preenche lote/série quando o código composto trouxe o identificador.
    const codigo = this.prefill?.codigoLoteSerie?.trim();
    if (codigo) {
      if (this.usaLotes) {
        const duplicado = this.lotes.some(l => (l.codigoLote || '').trim().toLowerCase() === codigo.toLowerCase());
        if (!duplicado) this.lotes = [...this.lotes, { codigoLote: codigo, saldoSistema: 0, saldoContado: 1 }];
      } else if (this.usaSeries) {
        const duplicado = this.series.some(s => (s.codigoSerie || '').trim().toLowerCase() === codigo.toLowerCase());
        if (!duplicado) this.series = [...this.series, { codigoSerie: codigo, saldoSistema: 0, saldoContado: 1 }];
      }
    }
  }

  public get usaLotes(): boolean { return !!this.produto?.isControlaLotes; }
  public get usaSeries(): boolean { return !!this.produto?.isControlaSeries; }
  public get usaQuantidadeSimples(): boolean { return !!this.produto && !this.usaLotes && !this.usaSeries; }

  public get totalLotes(): number {
    return this.lotes.reduce((s, l) => s + (Number(l.saldoContado) || 0), 0);
  }

  public get totalSeries(): number {
    return this.series.reduce((s, item) => s + (Number(item.saldoContado) || 0), 0);
  }

  public get quantidadeFinal(): number {
    if (this.usaLotes) return this.totalLotes;
    if (this.usaSeries) return this.totalSeries;
    return this.quantidade;
  }

  public get localAtual(): ItemSheetLocalOpcao | undefined {
    return this.locaisDisponiveis.find(l => l.idLocalizacaoEstoque === this.idLocalizacaoEstoque);
  }

  public get saldoSistema(): number {
    return this.localAtual?.saldoInicial ?? 0;
  }

  public get quantidadeAtualSalva(): number | null {
    return this.localAtual?.quantidadeInventariada ?? null;
  }

  public get ajustePrevisto(): number | null {
    if (this.idLocalizacaoEstoque == null) return null;
    return this.quantidadeFinal - this.saldoSistema;
  }

  public get ajustePrevistoTexto(): string {
    const ajuste = this.ajustePrevisto;
    if (ajuste == null) return '—';
    const prefixo = ajuste > 0 ? '+' : '';
    return `${prefixo}${ajuste.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 3 })}`;
  }

  public incrementar(): void { this.quantidade = (Number(this.quantidade) || 0) + 1; }
  public decrementar(): void { this.quantidade = Math.max(0, (Number(this.quantidade) || 0) - 1); }

  public abrirRastreabilidade(): void {
    const produto = this.produto;
    const local = this.localAtual;
    if (!produto || !local || (!this.usaLotes && !this.usaSeries)) return;

    if (this.usaLotes) {
      const data: LoteInventarioModalData = {
        idInventarioItem: local.idInventarioItem ?? 0,
        idProduto: produto.idProduto,
        idLocalizacaoEstoque: local.idLocalizacaoEstoque,
        nomeProduto: produto.descricao,
        skuProduto: produto.codigoSku,
        saldoSistema: local.saldoInicial ?? 0,
        lotes: this.lotes.map(item => ({ ...item })),
        isReadOnly: this.isReadOnly,
      };
      this.dialog.open(LoteInventarioModalComponent, {
        width: '1040px',
        maxWidth: '95vw',
        maxHeight: '90vh',
        data,
      }).afterClosed().subscribe(result => {
        if (!result?.confirmed) return;
        this.lotes = (result.lotes as LoteInventarioModalItem[]).map(item => ({ ...item }));
        this.confirmarRastreabilidadeLocal();
      });
      return;
    }

    const data: SerieInventarioModalData = {
      idInventarioItem: local.idInventarioItem ?? 0,
      idProduto: produto.idProduto,
      idLocalizacaoEstoque: local.idLocalizacaoEstoque,
      nomeProduto: produto.descricao,
      skuProduto: produto.codigoSku,
      saldoSistema: local.saldoInicial ?? 0,
      series: this.series.map(item => ({ ...item })),
      isReadOnly: this.isReadOnly,
    };
    this.dialog.open(SerieInventarioModalComponent, {
      width: '640px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data,
    }).afterClosed().subscribe(result => {
      if (!result?.confirmed) return;
      this.series = (result.series as SerieInventarioModalItem[]).map(item => ({ ...item }));
      this.confirmarRastreabilidadeLocal();
    });
  }

  private confirmarRastreabilidadeLocal(): void {
    const local = this.localAtual;
    if (!local) return;

    local.lotes = this.lotes.map(item => ({ ...item }));
    local.series = this.series.map(item => ({ ...item }));
    local.quantidadeInventariada = this.quantidadeFinal;
    local.confirmadoRastreabilidade = true;
    this.rastreabilidadeConfirmada = true;
  }

  public get podeAdicionar(): boolean {
    if (!this.produto || this.isCarregandoProduto) return false;
    if (this.idLocalizacaoEstoque == null) return false;
    if (this.usaLotes || this.usaSeries) return true;
    return this.quantidade != null && this.quantidade >= 0;
  }

  public onLimpar(): void {
    this.quantidade = 1;
    this.lotes = [];
    this.series = [];
    this.rastreabilidadeConfirmada = false;
    this.limpar.emit();
  }

  public onConfirmar(): void {
    if (!this.podeAdicionar || this.idLocalizacaoEstoque == null) return;
    this.confirmar.emit({
      idLocalizacaoEstoque: this.idLocalizacaoEstoque,
      quantidade: (this.usaLotes || this.usaSeries) && !this.rastreabilidadeConfirmada
        ? 0
        : this.quantidadeFinal,
      lotes: this.usaLotes && this.rastreabilidadeConfirmada ? this.lotes : undefined,
      series: this.usaSeries && this.rastreabilidadeConfirmada ? this.series : undefined,
    });
  }

  public onFechar(): void {
    this.fechar.emit();
  }
}
